<template>
    <div class="kgr-inputGroup">
        <kgr-form-row
            :label="$t('Jahr')"
            :error="error"
            :inline="true"
            field-name="year"
        >
            <kgr-generic-select
                v-model="selectedYear"
                :options="selectYears"
                :inline="true"
                @post-input="reloadHolidays()"
            />
        </kgr-form-row>
        <kgr-button
            class="button_save buttonMTL floatRight"
            :value="$t('save')"
            :disabled="!isSavable()"
            :pending="pending"
            @click="save()"
        />
        <kgr-button
            v-if="isCreatable"
            class="button_save buttonMTL floatRight"
            :value="$t('kindergarten.holiday.import')"
            :disabled="importDisabled"
            :pending="pendingImport"
            @click="showImportLightbox()"
        />

        <table class="kgr-table kgr-table--block kgr-table--striped">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('kindergarten.holiday.date') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('kindergarten.holiday.description') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <template v-for="holiday in holidays">
                    <kgr-kindergarten-holiday-edit-row
                        :holiday="holiday"
                        :edit-holidays="editHolidays"
                        @remove="remove(holiday)"
                    />

                    <kgr-kindergarten-holiday-show-row
                        :holiday="holiday"
                        :edit-holidays="editHolidays"
                        :is-deletable="isDeletable"
                        :is-editable="isEditable"
                        @edit="edit(holiday)"
                        @remove="remove(holiday)"
                    />
                </template>

                <tr
                    v-if="isCreatable"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <kgr-date-picker v-model="newHoliday.date" />
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-text-input v-model.trim="newHoliday.description" />
                    </td>
                    <td class="kgr-table__cell"></td>
                </tr>
            </tbody>
        </table>

        <kgr-button
            class="button_save buttonMTL floatRight"
            :value="$t('save')"
            :disabled="!isSavable()"
            :pending="pending"
            @click="save()"
        />
        <br /><br />

        <kgr-kindergarten-holiday-import-dialog
            v-if="!importDisabled"
            :year="selectedYear"
            :show="showLightbox"
            @import="importHolidays()"
            @close="closeImportLightbox()"
        />
    </div>
</template>

<script>
import DatePicker from '../../common/form/picker/DatePicker';
import Error from '../../../js/model/Error';
import FormRow from '../../common/form/FormRow';
import GenericSelect from '../../common/form/select/GenericSelect';
import KgrButton from '../../common/form/button/KgrButton';
import KindergartenHoliday from '../../../js/dto/KindergartenHoliday';
import KindergartenHolidayEditRow from './KindergartenHolidayEditRow';
import KindergartenHolidayImportDialog from './KindergartenHolidayImportDialog';
import KindergartenHolidayRepository from '../../../js/repository/KindergartenHolidayRepository';
import KindergartenHolidayShowRow from './KindergartenHolidayShowRow';
import TextInput from '../../common/form/input/TextInput';

export default {
    components: {
        'kgr-form-row': FormRow,
        'kgr-date-picker': DatePicker,
        'kgr-button': KgrButton,
        'kgr-text-input': TextInput,
        'kgr-kindergarten-holiday-show-row': KindergartenHolidayShowRow,
        'kgr-kindergarten-holiday-edit-row': KindergartenHolidayEditRow,
        'kgr-generic-select': GenericSelect,
        'kgr-kindergarten-holiday-import-dialog': KindergartenHolidayImportDialog,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
        kindergartenHolidays: {
            type: Array,
            required: true,
        },
        year: {
            type: Number,
            required: false,
            default: null,
        },
        years: {
            type: Array,
            required: true,
        },
        importYears: {
            type: Array,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
        isDeletable: {
            type: Boolean,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
    },
    data() {
        return {
            holidays: this.kindergartenHolidays,
            selectYears: this.years,
            newHoliday: new KindergartenHoliday(),
            editHolidays: [],
            pending: false,
            pendingImport: false,
            selectedYear: this.year,
            showLightbox: false,
        };
    },
    computed: {
        importDisabled() {
            return this.selectedYear === null || !this.importYears.includes(this.selectedYear);
        },
    },
    methods: {
        isBeingEdited(holiday) {
            return this.getEditIndex(holiday) !== -1;
        },
        getEditIndex(holiday) {
            return this.editHolidays.map((editHoliday) => editHoliday.id).indexOf(holiday.id);
        },
        remove(holiday) {
            showConfirm(this.$t('confirm'), this.$t('kindergarten.holiday.delete.confirm', { name: holiday.description }), () => {
                KindergartenHolidayRepository.delete(holiday.id)
                    .then(() => {
                        this.$notify.success('kindergarten.holiday.delete.success');
                        this.reloadYears();
                        this.reloadHolidays();
                    })
                    .catch(() => this.$notify.error('saveChanges.error'));
            }, this.$t('delete'));
        },
        create() {
            this.pending = true;
            KindergartenHolidayRepository.create(this.kindergartenId, this.newHoliday)
                .then(() => {
                    this.newHoliday = new KindergartenHoliday();
                    this.$notify.success('saveChanges.success');
                    this.reloadYears();
                    this.reloadHolidays();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pending = false);
        },
        update() {
            this.pending = true;
            KindergartenHolidayRepository.update(this.editHolidays)
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.reloadYears();
                    this.reloadHolidays();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pending = false);
        },
        reloadHolidays() {
            KindergartenHolidayRepository.findByKindergartenAndYear(this.kindergartenId, this.selectedYear)
                .then((response) => {
                    this.editHolidays = [];
                    this.holidays = response;
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        reloadYears() {
            KindergartenHolidayRepository.findYearsByKindergarten(this.kindergartenId)
                .then((response) => this.selectYears = response)
                .catch(() => this.$notify.error('error.loadingData'));
        },
        importHolidays() {
            this.pendingImport = true;
            KindergartenHolidayRepository.importHolidaysForKindergartenAndYear(this.kindergartenId, this.selectedYear)
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.reloadHolidays();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pendingImport = false);
        },
        isSavable() {
            if (!this.isEditable && this.isCreatable) {
                return false;
            }
            if (this.editHolidays.length === 0 && !this.newHoliday.isValid()) {
                return false;
            }
            if (this.editHolidays.filter((editHoliday) => !editHoliday.date || !editHoliday.description).length > 0) {
                return false;
            }
            if (this.newHoliday.isNotEmpty() && !this.newHoliday.isValid()) {
                return false;
            }
            return true;
        },
        edit(holiday) {
            if (this.isBeingEdited(holiday)) {
                return;
            }
            this.editHolidays.push(holiday.toData());
        },
        save() {
            if (this.newHoliday.isValid()) {
                this.create();
            }
            if (this.editHolidays.length > 0) {
                this.update();
            }
        },
        showImportLightbox() {
            this.showLightbox = true;
        },
        closeImportLightbox() {
            this.showLightbox = false;
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .buttonMTL {
    margin-top: 20px;
    margin-left: 20px;
}
.floatRight {
    float: right;
}
</style>
